import React, { useState, useEffect, useRef, useCallback } from 'react';
import { _T, C } from '../../../shared/theme';
import { s_PB, s_GC } from '../../../shared/styles';
import { saveGameScore } from '../../../shared/utils';
import { useStorage } from '../../../shared/hooks';
import Avatar from '../../../components/ui/Avatar';
import MultiplayerInviteModal from '../../../modals/MultiplayerInviteModal';
import MultiplayerResults from '../../../modals/MultiplayerResults';

// ── Web Audio helpers ─────────────────────────────────────────────────────────
function playBeep(type) {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const osc = ctx.createOscillator();
    const env = ctx.createGain();
    osc.connect(env); env.connect(ctx.destination);

    if (type === 'correct') {
      // Bright bell: quick rise, clean decay
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(1320, ctx.currentTime + 0.06);
      env.gain.setValueAtTime(0, ctx.currentTime);
      env.gain.linearRampToValueAtTime(0.25, ctx.currentTime + 0.01);
      env.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.32);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.35);
    } else {
      // Low thud / wrong buzz
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(160, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(80, ctx.currentTime + 0.18);
      env.gain.setValueAtTime(0, ctx.currentTime);
      env.gain.linearRampToValueAtTime(0.18, ctx.currentTime + 0.01);
      env.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.22);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.25);
    }
    ctx.resume().then(() => {}).catch(() => {});
    setTimeout(() => { try { ctx.close(); } catch {} }, 600);
  } catch {}
}

// ── Ripple particle on correct tap ───────────────────────────────────────────
function TapRipple({ x, y, color, id }) {
  return (
    <div key={id} style={{
      position: 'absolute',
      left: x, top: y,
      width: 0, height: 0,
      pointerEvents: 'none',
      zIndex: 20,
    }}>
      {/* Expanding ring */}
      <div style={{
        position: 'absolute',
        transform: 'translate(-50%,-50%)',
        width: 20, height: 20,
        borderRadius: '50%',
        border: `2px solid ${color}`,
        animation: 'ft-ring 0.55s ease-out both',
      }} />
      {/* 6 particles */}
      {[0, 60, 120, 180, 240, 300].map(deg => (
        <div key={deg} style={{
          position: 'absolute',
          transform: 'translate(-50%,-50%)',
          width: 7, height: 7,
          borderRadius: '50%',
          background: color,
          '--tx': `${Math.cos(deg * Math.PI / 180) * 32}px`,
          '--ty': `${Math.sin(deg * Math.PI / 180) * 32}px`,
          animation: 'ft-part 0.48s ease-out both',
        }} />
      ))}
    </div>
  );
}

// ── FocusTapGame ──────────────────────────────────────────────────────────────
function FocusTapGame({ lang, onBack, allUsers, friends, currentUserId, userName, invitePartner }) {
  const nl = lang === 'nl';
  const [active, setActive]       = useState(false);
  const [finished, setFinished]   = useState(false);
  const [score, setScore]         = useState(0);
  const [wrong, setWrong]         = useState(0);
  const [streak, setStreak]       = useState(0);
  const [timeLeft, setTimeLeft]   = useState(45);
  const [target, setTarget]       = useState(null);
  const [fb, setFb]               = useState(null); // 'correct'|'wrong'
  const [ripples, setRipples]     = useState([]);
  const [bgFlash, setBgFlash]     = useState(false);
  const [best, setBest]           = useStorage('gm_focustap_best', 0);
  const [showInvite, setShowInvite] = useState(false);
  const [partner, setPartner]     = useState(null);
  const [showResults, setShowResults] = useState(null);

  useEffect(() => {
    if (invitePartner) setPartner(invitePartner);
  }, [invitePartner]); // eslint-disable-line

  const scoreRef  = useRef(0);
  const wrongRef  = useRef(0);
  const streakRef = useRef(0);
  const timerRef  = useRef(null);
  const targetRef = useRef(null);
  const rippleId  = useRef(0);

  const NOGO_COLORS = ['#E05555', '#D4873A', '#5B9ED0', '#C06090'];
  const GO_COLOR    = '#4AC87A';

  const nextTarget = useCallback(() => {
    const isGo  = Math.random() > 0.36;
    const color = isGo ? GO_COLOR : NOGO_COLORS[Math.floor(Math.random() * NOGO_COLORS.length)];
    const size  = isGo ? 52 + Math.random() * 16 : 44 + Math.random() * 18;
    setTarget({
      type: isGo ? 'go' : 'nogo',
      color, size,
      pos: { x: 8 + Math.random() * 74, y: 8 + Math.random() * 74 },
    });
    setFb(null);
    targetRef.current = setTimeout(() => { setTarget(null); nextTarget(); }, 900 + Math.random() * 200);
  }, []); // eslint-disable-line

  const start = () => {
    scoreRef.current = 0; wrongRef.current = 0; streakRef.current = 0;
    setScore(0); setWrong(0); setStreak(0); setTimeLeft(45);
    setActive(true); setFinished(false); setShowResults(null);
    setFb(null); setRipples([]); setBgFlash(false);
    nextTarget();
    timerRef.current = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) {
          clearInterval(timerRef.current);
          clearTimeout(targetRef.current);
          setActive(false); setFinished(true); setTarget(null);
          return 0;
        }
        return t - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    if (!finished) return;
    const net = Math.max(0, scoreRef.current - wrongRef.current);
    if (net > best) setBest(net);
    saveGameScore('focustap', net);
    if (partner) {
      setShowResults({ myScore: net, partnerScore: Math.max(0, net + Math.floor(Math.random() * 10) - 5) });
    } else {
      // Auto-trigger GameEndModal via onBack (no quit flag = natural finish)
      setTimeout(() => onBack(), 400);
    }
  }, [finished]); // eslint-disable-line

  useEffect(() => () => { clearInterval(timerRef.current); clearTimeout(targetRef.current); }, []);

  const tapTarget = (e) => {
    if (!active || !target) return;
    e.stopPropagation();
    clearTimeout(targetRef.current);
    const rect = e.currentTarget.closest('[data-arena]').getBoundingClientRect();
    const px   = e.clientX - rect.left;
    const py   = e.clientY - rect.top;

    if (target.type === 'go') {
      scoreRef.current++; streakRef.current++;
      setScore(s => s + 1); setStreak(streakRef.current); setFb('correct');
      playBeep('correct');
      const id = ++rippleId.current;
      setRipples(r => [...r.slice(-4), { id, x: px, y: py, color: GO_COLOR }]);
      setTimeout(() => setRipples(r => r.filter(p => p.id !== id)), 600);
    } else {
      wrongRef.current++; streakRef.current = 0;
      setWrong(w => w + 1); setStreak(0); setFb('wrong');
      playBeep('wrong');
      setBgFlash(true);
      setTimeout(() => setBgFlash(false), 320);
    }
    setTarget(null);
    setTimeout(nextTarget, 340);
  };

  const tapBg = (e) => {
    if (!active || !target) return;
    if (target.type === 'nogo') return;
    wrongRef.current++; streakRef.current = 0;
    setWrong(w => w + 1); setStreak(0); setFb('wrong');
    playBeep('wrong');
    setBgFlash(true);
    setTimeout(() => setBgFlash(false), 320);
    clearTimeout(targetRef.current); setTarget(null);
    setTimeout(nextTarget, 340);
  };

  const maxStreak = Math.max(streakRef.current, streak);

  if (showResults) return (
    <MultiplayerResults
      lang={lang} myScore={showResults.myScore} myLabel={nl ? 'treffers' : 'hits'}
      partnerId={partner?.id} partnerScore={Math.max(0, showResults.partnerScore)}
      partnerLabel={nl ? 'treffers' : 'hits'}
      aiComment={nl
        ? `Score van ${showResults.myScore}! Focus Tap traint selectieve aandacht en impulscontrole.`
        : `Score of ${showResults.myScore}! Focus Tap trains selective attention and impulse control.`}
      onClose={() => { setShowResults(null); setPartner(null); }} onRematch={start}
    />
  );

  const pct = ((45 - timeLeft) / 45) * 100;

  return (
    <div>
      <style>{`
        @keyframes ft-pop   { 0%{transform:translate(-50%,-50%) scale(0.4)} 60%{transform:translate(-50%,-50%) scale(1.12)} 100%{transform:translate(-50%,-50%) scale(1)} }
        @keyframes ft-pulse { 0%,100%{box-shadow:0 0 22px var(--gc)66,0 0 8px var(--gc)33} 50%{box-shadow:0 0 42px var(--gc)99,0 0 18px var(--gc)55} }
        @keyframes ft-shake { 0%,100%{transform:translateX(0)} 20%{transform:translateX(-6px)} 40%{transform:translateX(6px)} 60%{transform:translateX(-4px)} 80%{transform:translateX(4px)} }
        @keyframes ft-ring  { 0%{width:20px;height:20px;opacity:1} 100%{width:90px;height:90px;opacity:0} }
        @keyframes ft-part  { 0%{transform:translate(-50%,-50%) translateX(0) translateY(0);opacity:1} 100%{transform:translate(-50%,-50%) translateX(var(--tx)) translateY(var(--ty));opacity:0} }
        @keyframes ft-score { 0%{transform:scale(1.6);color:#4AC87A} 100%{transform:scale(1);color:inherit} }
        @keyframes ft-wrong { 0%{transform:scale(1.4);color:#E05555} 100%{transform:scale(1);color:inherit} }
        @keyframes ft-flash { 0%{opacity:0.28} 100%{opacity:0} }
        @keyframes ft-streak{ 0%{transform:scale(1.5)} 100%{transform:scale(1)} }
      `}</style>

      {showInvite && (
        <MultiplayerInviteModal lang={lang} title="Focus Tap" icon="🎯"
          allUsers={allUsers} friends={friends}
          onInvite={(user) => { setPartner(user); setShowInvite(false); }}
          onClose={() => setShowInvite(false)} />
      )}

      {/* Header — back during active play = quit penalty */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <button
          onClick={() => onBack()}
          style={{ background: 'none', border: 'none', color: C.silver, cursor: 'pointer', fontSize: 13 }}
        >■ {nl ? 'Stop' : 'Stop'}</button>
        <button onClick={() => setShowInvite(true)} style={{ background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, color: C.white, borderRadius: 10, padding: '5px 12px', cursor: 'pointer', fontSize: 11 }}>
          👥 {nl ? 'Samen' : 'Together'}
        </button>
      </div>

      {partner && (
        <div style={{ ...s_GC({ padding: '8px 12px', marginBottom: 10 }), display: 'flex', alignItems: 'center', gap: 8 }}>
          <Avatar user={partner} size={26} />
          <span style={{ fontSize: 12, color: C.teal }}>{partner.name} {nl ? 'speelt mee' : 'is playing'} 🟢</span>
        </div>
      )}

      {/* Start screen */}
      {!active && !finished && (
        <div style={{ background: `linear-gradient(160deg, ${_T.bg3}, ${_T.bg2})`, border: `1px solid rgba(74,200,122,0.2)`, borderRadius: 20, padding: '36px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: 56, marginBottom: 12, filter: 'drop-shadow(0 0 16px #4AC87A88)' }}>🎯</div>
          <div style={{ color: '#fff', fontSize: 20, fontWeight: 800, marginBottom: 10, fontFamily: 'Georgia,serif' }}>Focus Tap</div>
          <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 13, lineHeight: 1.8, marginBottom: 20 }}>
            {nl
              ? 'Tik alleen de 🟢 groene cirkels.\nNegeer alles wat rood, oranje of blauw is.'
              : 'Tap only 🟢 green circles.\nIgnore anything red, orange or blue.'}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 24 }}>
            {[
              { icon: '✅', label: nl ? 'Groen = tikken' : 'Green = tap', color: '#4AC87A' },
              { icon: '🚫', label: nl ? 'Rood/Blauw = negeer' : 'Red/Blue = ignore', color: '#E05555' },
            ].map(t => (
              <div key={t.label} style={{ background: `${t.color}12`, border: `1px solid ${t.color}30`, borderRadius: 12, padding: '10px 12px', color: t.color, fontSize: 12, fontWeight: 600 }}>
                {t.icon} {t.label}
              </div>
            ))}
          </div>
          {best > 0 && (
            <div style={{ color: '#C8A84B', fontSize: 13, fontWeight: 700, marginBottom: 16 }}>
              🏆 {nl ? 'Beste' : 'Best'}: {best}
            </div>
          )}
          <button style={s_PB({ fontSize: 15, padding: '14px 32px' })} onClick={start}>
            {nl ? '▶ Starten' : '▶ Start'}
          </button>
        </div>
      )}

      {/* Active game */}
      {active && (
        <>
          {/* Score bar */}
          <div style={{ background: `linear-gradient(135deg, ${_T.bg3}, ${_T.bg2})`, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 16, padding: '12px 16px', marginBottom: 10 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8, textAlign: 'center', marginBottom: 10 }}>
              <div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', fontWeight: 700, letterSpacing: 1 }}>HITS</div>
                <div style={{ fontSize: 22, color: '#4AC87A', fontWeight: 800, animation: fb === 'correct' ? 'ft-score 0.3s ease both' : undefined }}>{score}</div>
              </div>
              <div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', fontWeight: 700, letterSpacing: 1 }}>MISS</div>
                <div style={{ fontSize: 22, color: '#E05555', fontWeight: 800, animation: fb === 'wrong' ? 'ft-wrong 0.3s ease both' : undefined }}>{wrong}</div>
              </div>
              <div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', fontWeight: 700, letterSpacing: 1 }}>🔥 STREAK</div>
                <div key={streak} style={{ fontSize: 22, color: streak >= 5 ? '#C8A84B' : 'rgba(255,255,255,0.7)', fontWeight: 800, animation: streak > 0 ? 'ft-streak 0.25s ease both' : undefined }}>{streak}</div>
              </div>
              <div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', fontWeight: 700, letterSpacing: 1 }}>TIME</div>
                <div style={{ fontSize: 22, color: timeLeft < 10 ? '#E05555' : 'rgba(255,255,255,0.9)', fontWeight: 800 }}>{timeLeft}s</div>
              </div>
            </div>
            {/* Time progress bar */}
            <div style={{ height: 4, background: 'rgba(255,255,255,0.07)', borderRadius: 4, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${pct}%`, borderRadius: 4, background: timeLeft < 10 ? 'linear-gradient(90deg,#E05555,#ff8888)' : 'linear-gradient(90deg,#4AC87A,#7EE8A2)', transition: 'width 1s linear, background 0.3s' }} />
            </div>
          </div>

          {/* Arena */}
          <div
            data-arena
            onClick={tapBg}
            style={{
              position: 'relative', width: '100%', height: 320,
              background: bgFlash
                ? 'radial-gradient(circle at center, rgba(224,85,85,0.25) 0%, rgba(10,15,28,0.98) 70%)'
                : `radial-gradient(circle at 30% 40%, rgba(74,200,122,0.06) 0%, rgba(10,15,28,0.98) 65%)`,
              borderRadius: 20,
              border: `1.5px solid ${bgFlash ? 'rgba(224,85,85,0.4)' : 'rgba(74,200,122,0.15)'}`,
              overflow: 'hidden', cursor: 'crosshair',
              transition: 'background 0.15s, border-color 0.15s',
              animation: bgFlash ? 'ft-shake 0.32s ease' : undefined,
            }}
          >
            {/* Wrong flash overlay */}
            {bgFlash && (
              <div style={{ position: 'absolute', inset: 0, background: 'rgba(224,85,85,0.18)', animation: 'ft-flash 0.32s ease both', pointerEvents: 'none', borderRadius: 20, zIndex: 2 }} />
            )}

            {/* Feedback icon */}
            {fb && (
              <div style={{
                position: 'absolute', top: '50%', left: '50%',
                transform: 'translate(-50%,-50%)',
                fontSize: 44, zIndex: 15, pointerEvents: 'none',
                color: fb === 'correct' ? '#4AC87A' : '#E05555',
                animation: 'ft-pop 0.28s ease both',
                filter: `drop-shadow(0 0 12px ${fb === 'correct' ? '#4AC87A' : '#E05555'}88)`,
              }}>
                {fb === 'correct' ? '✓' : '✗'}
              </div>
            )}

            {/* Ripple particles */}
            {ripples.map(r => <TapRipple key={r.id} {...r} />)}

            {/* Target circle */}
            {target && (
              <div
                onClick={tapTarget}
                style={{
                  position: 'absolute',
                  left: `${target.pos.x}%`, top: `${target.pos.y}%`,
                  width: target.size, height: target.size,
                  borderRadius: '50%',
                  background: target.type === 'go'
                    ? `radial-gradient(circle, ${target.color}cc 0%, ${target.color}66 60%, ${target.color}22 100%)`
                    : `radial-gradient(circle, ${target.color}99 0%, ${target.color}44 70%, ${target.color}11 100%)`,
                  border: `2.5px solid ${target.color}${target.type === 'go' ? 'dd' : '77'}`,
                  cursor: 'pointer',
                  transform: 'translate(-50%,-50%)',
                  zIndex: 5,
                  '--gc': target.color,
                  animation: target.type === 'go'
                    ? 'ft-pop 0.22s ease both, ft-pulse 1.2s ease-in-out 0.22s infinite'
                    : 'ft-pop 0.22s ease both',
                  boxShadow: `0 0 22px ${target.color}55, 0 0 8px ${target.color}33`,
                }}
              />
            )}

            {/* Streak badge — shows when streak >= 3 */}
            {streak >= 3 && (
              <div style={{
                position: 'absolute', top: 10, right: 12, zIndex: 10,
                background: 'rgba(200,168,75,0.18)', border: '1px solid rgba(200,168,75,0.5)',
                borderRadius: 20, padding: '4px 10px',
                color: '#C8A84B', fontSize: 11, fontWeight: 800,
                pointerEvents: 'none',
              }}>
                🔥 ×{streak}
              </div>
            )}
          </div>

          <div style={{ textAlign: 'center', fontSize: 11, color: 'rgba(255,255,255,0.3)', marginTop: 8 }}>
            {nl ? '🟢 tikken · 🔴🟠🔵 negeren' : '🟢 tap · 🔴🟠🔵 ignore'}
          </div>
        </>
      )}

      {/* Finish: GameEndModal handles the end screen via onBack() called in useEffect above */}
      {finished && !showResults && (
        <div style={{ padding: '40px 24px', textAlign: 'center', color: 'rgba(255,255,255,0.25)', fontSize: 13 }}>
          {nl ? 'Sessie wordt afgerond…' : 'Finishing session…'}
        </div>
      )}
    </div>
  );
}

export default FocusTapGame;
